import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, TrendingUp, Wallet, GaugeCircle, Trophy, Filter, Download,
  ChevronRight, Loader2, Sparkles,
} from "lucide-react";

const currency = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function KpiCard({ label, value, subtext, icon: Icon, tone = "gold", testid }) {
  return (
    <div
      className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5 hover:border-[#d4af37]/40 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${tone === "gold" ? "text-[#d4af37]" : "text-gray-400"}`} strokeWidth={1.5} />
      </div>
      <p className="font-display text-3xl text-white leading-none" data-testid={`${testid}-value`}>{value}</p>
      {subtext && <p className="text-[11px] font-mono text-gray-500 mt-2">{subtext}</p>}
    </div>
  );
}

function FunnelBar({ block, max, active, onClick, testid }) {
  const pct = max ? Math.max((block.count / max) * 100, 5) : 5;
  const bg = active
    ? "linear-gradient(90deg, #d4af37 0%, #f2d478 100%)"
    : "linear-gradient(90deg, #333 0%, #444 100%)";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-2 rounded transition-all ${
        active ? "bg-[#d4af37]/5" : "hover:bg-[#131313]"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-mono uppercase tracking-widest w-32 ${active ? "text-[#d4af37]" : "text-gray-400"}`}>
          {block.label}
        </span>
        <div className="flex-1 h-2 rounded-full bg-[#1c1c1c] overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: bg }} />
        </div>
        <span className={`text-sm font-mono w-12 text-right ${active ? "text-[#d4af37]" : "text-white"}`}>
          {block.count}
        </span>
        <ChevronRight className={`w-3 h-3 ${active ? "text-[#d4af37]" : "text-gray-600"}`} strokeWidth={1.5} />
      </div>
    </button>
  );
}

export default function ExecutivePage() {
  const [filters, setFilters] = useState({ date_from: "", date_to: "", city: "", value_bracket: "" });
  const [filterOptions, setFilterOptions] = useState({ cities: [], value_brackets: [], phases: [] });
  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [bottlenecks, setBottlenecks] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseProjects, setPhaseProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildQ = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.date_from) p.append("date_from", new Date(filters.date_from).toISOString());
    if (filters.date_to) p.append("date_to", new Date(filters.date_to + "T23:59:59").toISOString());
    if (filters.city) p.append("city", filters.city);
    if (filters.value_bracket) p.append("value_bracket", filters.value_bracket);
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = buildQ();
      const [k, f, m, b, opts] = await Promise.all([
        api.get(`/analytics/kpis?${q}`),
        api.get(`/analytics/funnel?${q}`),
        api.get("/analytics/team-matrix"),
        api.get("/analytics/bottlenecks"),
        api.get("/analytics/filters"),
      ]);
      setKpis(k.data);
      setFunnel(f.data);
      setMatrix(m.data);
      setBottlenecks(b.data);
      setFilterOptions(opts.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [buildQ]);

  useEffect(() => { load(); }, [load]);

  const openPhase = async (phaseKey) => {
    setSelectedPhase(phaseKey);
    try {
      const { data } = await api.get(`/analytics/funnel/${phaseKey}/projects`);
      setPhaseProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const maxFunnelCount = useMemo(() => Math.max(...funnel.map((f) => f.count || 0), 1), [funnel]);

  const setQuickRange = (kind) => {
    const now = new Date();
    let from = null;
    if (kind === "mtd") from = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (kind === "qtd") {
      const q = Math.floor(now.getMonth() / 3) * 3;
      from = new Date(now.getFullYear(), q, 1);
    } else if (kind === "ytd") from = new Date(now.getFullYear(), 0, 1);
    setFilters({
      ...filters,
      date_from: from ? from.toISOString().slice(0, 10) : "",
      date_to: kind === "clear" ? "" : now.toISOString().slice(0, 10),
    });
  };

  const downloadPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/analytics/export.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `interazzo_executive_digest_${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Executive digest downloaded · password protected");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 min-h-screen">
      <header className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-1">Executive · Admin Only</p>
          <h1 className="font-display text-3xl text-white">Analytics & Performance</h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-[#d4af37]" strokeWidth={1.5} />
            Aggregated across the 9-phase lifecycle · 6-day working-week math
          </p>
        </div>
        <button
          onClick={downloadPdf}
          disabled={exporting}
          data-testid="export-pdf-btn"
          className="iz-btn-primary flex items-center gap-2 text-xs disabled:opacity-40"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={1.5} />}
          Export Executive Digest (PDF)
        </button>
      </header>

      {/* Filter bar */}
      <div className="border border-[#262626] bg-[#0a0a0a] rounded-md p-4 mb-6 flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2 mr-2">
          <Filter className="w-3.5 h-3.5 text-[#d4af37]" strokeWidth={1.5} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Filters</span>
        </div>
        <div className="flex gap-1">
          {[
            { k: "mtd", l: "MTD" },
            { k: "qtd", l: "QTD" },
            { k: "ytd", l: "YTD" },
            { k: "clear", l: "All" },
          ].map((r) => (
            <button
              key={r.k}
              data-testid={`range-${r.k}`}
              onClick={() => setQuickRange(r.k)}
              className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border border-[#333] text-gray-400 hover:text-[#d4af37] hover:border-[#d4af37]/40 rounded"
            >
              {r.l}
            </button>
          ))}
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-gray-500 block mb-0.5">From</label>
          <input
            type="date"
            className="iz-input text-xs"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            data-testid="filter-from"
          />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-gray-500 block mb-0.5">To</label>
          <input
            type="date"
            className="iz-input text-xs"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            data-testid="filter-to"
          />
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-gray-500 block mb-0.5">City</label>
          <select
            className="iz-input text-xs"
            value={filters.city}
            onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            data-testid="filter-city"
          >
            <option value="">All</option>
            {filterOptions.cities.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div>
          <label className="text-[9px] font-mono uppercase tracking-widest text-gray-500 block mb-0.5">Value Bracket</label>
          <select
            className="iz-input text-xs"
            value={filters.value_bracket}
            onChange={(e) => setFilters({ ...filters, value_bracket: e.target.value })}
            data-testid="filter-bracket"
          >
            <option value="">All</option>
            {filterOptions.value_brackets.map((b) => (<option key={b.key} value={b.key}>{b.label}</option>))}
          </select>
        </div>
        <div className="flex-1" />
      </div>

      {/* KPI cards */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            testid="kpi-velocity"
            label="Avg Lead-to-Closure"
            value={`${kpis.avg_lead_to_closure_days}d`}
            subtext={`sample n=${kpis.avg_lead_to_closure_sample_size} · 6-day working week`}
            icon={Activity}
          />
          <KpiCard
            testid="kpi-pipeline"
            label="Active Pipeline Value"
            value={currency(kpis.active_pipeline_value)}
            subtext="all unclosed won projects"
            icon={Wallet}
          />
          <KpiCard
            testid="kpi-slippage"
            label="Global Timeline Slippage"
            value={`${kpis.global_timeline_slippage_days >= 0 ? "+" : ""}${kpis.global_timeline_slippage_days}d`}
            subtext={`across ${kpis.active_sites_evaluated} active sites`}
            icon={GaugeCircle}
            tone={kpis.global_timeline_slippage_days > 5 ? "red" : "gold"}
          />
          <KpiCard
            testid="kpi-winloss"
            label="Win / Loss Ratio"
            value={`${kpis.win_percent}%`}
            subtext={`${kpis.win_count} won · ${kpis.loss_count} lost · ${kpis.total_leads} total`}
            icon={Trophy}
          />
        </div>
      )}

      {/* Funnel */}
      <section className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5 mb-8" data-testid="funnel-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">9-Phase End-to-End Funnel</p>
            <h3 className="font-display text-xl text-white mt-1">Active Projects Across the Ecosystem</h3>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">click a phase to inspect</span>
        </div>
        <div className="space-y-1">
          {funnel.map((f) => (
            <FunnelBar
              key={f.key}
              block={f}
              max={maxFunnelCount}
              active={selectedPhase === f.key}
              onClick={() => openPhase(f.key)}
              testid={`funnel-${f.key}`}
            />
          ))}
        </div>
        {selectedPhase && (
          <div className="mt-6 border-t border-[#1c1c1c] pt-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">
              Held in {funnel.find((f) => f.key === selectedPhase)?.label} · {phaseProjects.length} record(s)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="phase-project-table">
                <thead>
                  <tr className="border-b border-[#262626]">
                    <th className="text-left py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">Name</th>
                    <th className="text-left py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">City</th>
                    <th className="text-right py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">Value</th>
                    <th className="text-right py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">Stagnation</th>
                  </tr>
                </thead>
                <tbody>
                  {phaseProjects.slice(0, 25).map((p) => (
                    <tr key={p.id} className="border-b border-[#1a1a1a]">
                      <td className="py-2 pr-3 text-gray-200">{p.name}</td>
                      <td className="py-2 pr-3 text-gray-400">{p.city}</td>
                      <td className="py-2 pr-3 text-right text-gray-200 font-mono">{p.amount ? currency(p.amount) : "—"}</td>
                      <td className={`py-2 pr-3 text-right font-mono ${
                        p.stagnation_days > 30 ? "text-red-400" :
                        p.stagnation_days > 14 ? "text-[#d4af37]" : "text-gray-400"
                      }`}>
                        {p.stagnation_days}d
                      </td>
                    </tr>
                  ))}
                  {phaseProjects.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-500">Nothing in this phase.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Team matrix */}
      <section className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5 mb-8" data-testid="team-matrix">
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Departmental Performance</p>
          <h3 className="font-display text-xl text-white mt-1">Efficiency Matrix (E_team)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#262626]">
                {["Department", "Lead", "Active Volume", "Avg TAT", "Δ vs Target", "Efficiency"].map((h) => (
                  <th key={h} className="text-left py-2 pr-3 text-[10px] font-mono uppercase tracking-widest text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((r) => {
                const eff = r.efficiency || 0;
                const effTone = eff >= 80 ? "text-[#d4af37]" : eff >= 50 ? "text-gray-200" : "text-red-400";
                const variance = r.target_variance || 0;
                const vTone = variance <= 0 ? "text-emerald-300" : variance <= 5 ? "text-[#d4af37]" : "text-red-400";
                return (
                  <tr key={r.department} className="border-b border-[#1a1a1a]" data-testid={`row-${r.department.replace(/\W+/g, "-").toLowerCase()}`}>
                    <td className="py-3 pr-3 text-white">{r.department}</td>
                    <td className="py-3 pr-3 text-gray-300">{r.stakeholder?.name || r.role}</td>
                    <td className="py-3 pr-3 text-gray-300">{r.active_label}</td>
                    <td className="py-3 pr-3 text-gray-300 font-mono">{r.avg_tat_days}d</td>
                    <td className={`py-3 pr-3 font-mono ${vTone}`}>{variance >= 0 ? "+" : ""}{variance}d</td>
                    <td className={`py-3 pr-3 font-mono ${effTone}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-12 text-right">{eff}%</span>
                        <div className="w-24 h-1.5 rounded-full bg-[#1c1c1c] overflow-hidden">
                          <div className="h-full bg-[#d4af37]" style={{ width: `${Math.min(eff, 100)}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Bottlenecks */}
      <section className="border border-[#262626] bg-[#0a0a0a] rounded-md p-5" data-testid="bottleneck-section">
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Phase-Specific Bottleneck Coefficient</p>
          <h3 className="font-display text-xl text-white mt-1">B_phase across the 9 stages</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {bottlenecks.map((b) => {
            const isSlow = b.avg_slippage_days > 0;
            const tone = b.sample_size === 0 ? "gray" : isSlow ? "red" : "green";
            return (
              <div key={b.key} className="border border-[#1c1c1c] rounded p-3 bg-[#0f0f0f]" data-testid={`bt-${b.key}`}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{b.label}</p>
                <p className={`font-display text-xl mt-1 ${
                  tone === "red" ? "text-red-400" : tone === "green" ? "text-emerald-300" : "text-gray-400"
                }`}>
                  {b.sample_size === 0 ? "—" : `${b.avg_slippage_days >= 0 ? "+" : ""}${b.avg_slippage_days}d`}
                </p>
                <p className="text-[10px] font-mono text-gray-500 mt-1">
                  target ≤ {b.target_days}d · n={b.sample_size}
                </p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
